import React, {useState} from "react";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {Box, Button, Drawer, Grid, Paper} from "@mui/material";
import {Outlet, useLocation, useNavigate} from "react-router";
import {useSelector} from "react-redux";
import ErrorBox from "../Common/ErrorBox";
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PaymentsIcon from '@mui/icons-material/Payments';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TuneIcon from '@mui/icons-material/Tune';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import GroupsIcon from '@mui/icons-material/Groups';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

const ADMIN_TABS = [
    { label: "Orders", icon: <ManageSearchIcon/>, path: "/admin/orderHistory", nodes: [] },
    { label: "Belcoins", icon: <BorderColorIcon/>, path: "/admin/control", nodes: [] },
    { label: "Products", icon: <AddBusinessIcon/>, path: "/admin/productControl", nodes: [] },
    { label: "Accrual history", icon: <AssessmentIcon/>, path: "/admin/report", nodes: [] },
    { label: "Inactive employees", icon: <NotInterestedIcon />, path: "/admin/inactive", nodes: [] },
    {
        label: "Budget",
        icon: <PointOfSaleIcon />,
        nodes: [
            { label: "Budget management", icon: <PriceCheckIcon />, path: "/admin/budgetControl", nodes: [] },
            { label: "History", icon: <PaymentsIcon />, path: "/admin/budgetHistory", nodes: [] },
        ]
    },
    { label: "Seniority", icon: <WorkspacePremiumIcon />, path: "/admin/bonus", nodes: [] },
    {
        label: "Experience",
        icon: <EmojiEventsIcon />,
        nodes: [
            { label: "Seasons", icon: <EmojiEventsIcon />, path: "/admin/experience/seasons", nodes: [] },
            { label: "Coupons", icon: <MilitaryTechIcon />, path: "/admin/experience/coupons", nodes: [] },
            { label: "Requests", icon: <AutoStoriesIcon />, path: "/admin/experience/requests", nodes: [] },
            { label: "Balances", icon: <GroupsIcon />, path: "/admin/experience/balances", nodes: [] },
            { label: "XP for period", icon: <AssessmentIcon />, path: "/admin/experience/summary", nodes: [] },
        ]
    },
    {
        label: "Configs",
        icon: <EmojiEventsIcon />,
        nodes: [
            { label: "XP Categories", icon: <TuneIcon />, path: "/admin/experience/categories", nodes: [] },
            { label: "XP Levels", icon: <TuneIcon />, path: "/admin/experience/levels", nodes: [] },
            { label: "XP Achievements", icon: <MilitaryTechIcon />, path: "/admin/experience/achievements", nodes: [] },
            { label: "XP Mascots", icon: <MilitaryTechIcon />, path: "/admin/experience/mascots", nodes: [] },
            { label: "Texts", icon: <MilitaryTechIcon />, path: "/admin/experience/texts", nodes: [] },
            { label: "Order auto-replies", icon: <TuneIcon />, path: "/admin/orderComments", nodes: [] },
            { label: "Service functions", icon: <TuneIcon />, path: "/admin/serviceFunctions", nodes: [] },
        ]
    },
];

const treeViewSx = {
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'clip',
    "&& .Mui-selected": { backgroundColor: "#ed6c022e" },
    "& .MuiTreeItem-content": { borderRadius: 1, py: 0.5 },
    "& .MuiTreeItem-label": { fontSize: 14 }
};

function AdminTree({ onItemSelect }) {
    const navigate = useNavigate();
    const handleClick = (path) => {
        navigate(path);
        if (onItemSelect) onItemSelect();
    };
    return (
        <SimpleTreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            sx={treeViewSx}
        >
            {ADMIN_TABS.map((menu, index) => {
                if (menu.nodes.length !== 0) {
                    return (
                        <TreeItem key={`group-${index}`} itemId={`main ${index + 1}`} label={menu.label}>
                            {menu.nodes.map((object, idx) => (
                                <TreeItem
                                    key={`group-${index}-item-${idx}`}
                                    itemId={`menu ${index + 1}-${idx + 1}`}
                                    label={object.label}
                                    onClick={() => handleClick(object.path)}
                                />
                            ))}
                        </TreeItem>
                    );
                }
                return (
                    <TreeItem
                        key={`item-${index}`}
                        itemId={`item ${index + 1}`}
                        label={menu.label}
                        onClick={() => handleClick(menu.path)}
                    />
                );
            })}
        </SimpleTreeView>
    );
}

export default function Admin() {
    const sessionState = useSelector((state) => state.data.UserSession.user);
    const isUserHasAdminRole = sessionState.isAdmin;
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleToggleDrawer = () => setMobileOpen((prev) => !prev);
    const handleCloseDrawer = () => setMobileOpen(false);

    if (!isUserHasAdminRole) {
        return <ErrorBox error={"Access denied"}/>;
    }

    return (
        <Box className={"adminTab"} sx={{ py: { xs: 1, md: 2 } }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}>
                <Button
                    onClick={handleToggleDrawer}
                    variant="outlined"
                    color="warning"
                    startIcon={<MenuIcon />}
                    sx={{ borderRadius: 25 }}
                >
                    Admin menu
                </Button>
            </Box>

            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={handleCloseDrawer}
                sx={{ display: { xs: 'block', md: 'none' } }}
                PaperProps={{ sx: { width: 280, p: 2 } }}
            >
                <AdminTree onItemSelect={handleCloseDrawer} />
            </Drawer>

            <Grid container spacing={2}>
                <Grid item md={3} lg={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, position: 'sticky', top: 16 }}>
                        <AdminTree />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={9} lg={10} sx={{ minWidth: 0 }}>
                    <Box sx={{ px: { xs: 0, md: 1 } }}>
                        <Outlet/>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
